import React from "react";
import { PROFILE, VALUES, IMAGES } from "../data/content";
import { Eyebrow, Reveal } from "./shared";
import { LeafSchematic, PlanetDot } from "./motifs";

export default function About() {
  return (
    <section id="about" className="relative py-24 sm:py-32 bg-ivory overflow-hidden">
      {/* topographic warm background */}
      <div className="pointer-events-none absolute inset-0 topo-lines opacity-60" aria-hidden />
      {/* faint constellation dot-grid, right side only */}
      <div className="pointer-events-none absolute right-0 top-0 w-1/2 h-full constellation-grid opacity-30" aria-hidden />
      {/* engineering botanical schematic, decorative */}
      <div className="pointer-events-none absolute -left-8 top-24 hidden lg:block" aria-hidden>
        <LeafSchematic className="w-40 h-56" stroke="#B08830" opacity={0.18} />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow n={2} className="mb-12">Who I Am</Eyebrow>
        </Reveal>

        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start">
          <Reveal>
            <div className="relative">
              <div className="relative rounded-[1.4rem] overflow-hidden border border-border shadow-[0_20px_60px_rgba(74,51,34,0.12)] observatory-frame">
                <img
                  src={IMAGES.about}
                  alt="Flat lay of a leather portfolio, gold pen, weekly planner, a sage succulent in a terracotta pot, and reading glasses"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                  loading="lazy"
                />
              </div>

              {/* values pills */}
              <div className="mt-6 flex flex-wrap gap-2" data-testid="values-pills">
                {VALUES.map((v, i) => (
                  <span
                    key={v}
                    className={`font-mono-tech text-[11px] tracking-wide uppercase rounded-full px-3 py-1.5 border transition-colors ${
                      i === 0
                        ? "bg-terracotta text-ivory border-terracotta warm-glow"
                        : "bg-linen text-caramel border-border hover:border-brass"
                    }`}
                    data-testid={`value-${i}`}
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-6 text-caramel/85 text-lg leading-relaxed">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-brass flex items-center gap-3">
                <span className="w-6 h-px bg-brass/50" /> Field notes
              </div>
              <p className="font-display text-3xl sm:text-4xl text-caramel leading-snug font-light">
                I didn't get here by following a straight line. My path has been shaped by curiosity, adaptability, and a genuine love for making things work better for the people around me and the organizations I've been part of. That unconventional journey is exactly what makes me effective.
              </p>
              <p>
                I have a deep love of learning new tools and skills; I pick them up quickly and love applying them to make workflows smarter and more efficient. Efficiency isn't just a work style for me — it's how I think. I'm always looking for the smarter path, the cleaner system, the process that removes friction before anyone else even notices it's there.
              </p>
              <p>
                One thing I've learned about myself: I thrive when I operate through influence rather than authority. I don't need a title to lead. I build trust across every level of an organization — from the front desk to the C-suite — and I move people and projects forward through genuine relationships, clear communication, and the kind of quiet consistency that makes teams feel supported without ever needing to be told why. This is where I do my best work.
              </p>
              <p>
                As a curator of company culture, I've always believed that the best work happens in environments that feel fun, inclusive, and engaging. I take pride in being the person who remembers birthdays, builds the committee, and plans the moment that makes a team feel like a team. Fun and fulfillment aren't perks to me; they're the foundation of a high-performing culture, and I bring that energy intentionally.
              </p>
              <p>
                I don't just look for roles because I can do them. I look for opportunities where I can contribute to something meaningful alongside people who care about their impact. I treat every organization I support as if it were my own — because when you work with me, that's exactly what you get.
              </p>
              <blockquote className="relative border-l-2 border-terracotta pl-6 py-1 font-display italic text-2xl sm:text-3xl text-terracotta font-light">
                "Give me the whole picture, and I will build the calm around it."
                <span
                  className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-terracotta"
                  aria-hidden
                />
              </blockquote>
              <p className="font-mono-tech text-xs tracking-widest uppercase text-warmmuted flex items-center gap-2">
                <PlanetDot /> {PROFILE.location} · Available for the right team
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
